import { useDatabase } from '../composables/database'
import { RecapResponse } from '../types/recap'
import { useStoreRecaps } from '../store/useStoreRecaps'
import { storeToRefs } from 'pinia'
import { logInfo } from '../utils'

type StoreRecapResponsesModelReturn = {
  getAllStoreRecapResponses: () => Promise<RecapResponse[]>
  getStoreRecapResponseForStoreAndRecap: (
    store_id: string,
    recap_id: string
  ) => Promise<RecapResponse | null>
  writeStoreRecapResponses: (responses: RecapResponse[]) => Promise<boolean>
  removeStaleStoreRecapResponses: () => Promise<void>
  removeStoreResponsesForSyncedResponseWithStoreId: (
    store_id: string,
    recap_id: string
  ) => Promise<void>
  clearStoreRecapResponses: () => Promise<void>
}

export type DatabaseStoreRecapResponse = {
  id: string
  store_id: string
  recap_id: string
  response: string
  date_uploaded: string
}

/**
 * Interacts with the recap respponses table
 * @returns {RecapResponsesModelReturn}
 */
export const StoreRecapResponsesModel = (): StoreRecapResponsesModelReturn => {
  const { run, query } = useDatabase()
  const recapStore = storeToRefs(useStoreRecaps())

  async function getAllStoreRecapResponses(): Promise<RecapResponse[]> {
    try {
      const sql = `
        SELECT
          *
        FROM
          store_recap_responses
      `
      const recapsResponse = ((await query(sql)) as unknown as DatabaseStoreRecapResponse[]) || []
      return recapsResponse.map((response) => JSON.parse(response.response))
    } catch (error) {
      logInfo('error', 'getAllStoreRecapResponses', 'Error fetching all store recap responses', {
        error,
      })
      return []
    }
  }

  async function getStoreRecapResponseForStoreAndRecap(
    store_id: string,
    recap_id: string
  ): Promise<RecapResponse | null> {
    try {
      const sql = `
        SELECT
          *
        FROM
          store_recap_responses
        WHERE
          store_id = ${store_id}
          AND recap_id = ${recap_id}
        LIMIT 1
      `
      const recapResponse = ((await query(sql)) as unknown as DatabaseStoreRecapResponse) || null
      return JSON.parse(recapResponse.response) as RecapResponse
    } catch (error) {
      logInfo(
        'error',
        'getStoreRecapResponseForStoreAndRecap',
        'Error fetching store recap response',
        { error, store_id, recap_id }
      )
      return null
    }
  }

  async function writeStoreRecapResponses(responses: RecapResponse[]): Promise<boolean> {
    const failedResponses: RecapResponse[] = []
    for (const response of responses) {
      try {
        const sql = `
          INSERT OR REPLACE INTO store_recap_responses(store_id, recap_id, response, date_uploaded)
          VALUES (?, ?, ?, date())
        `
        await run(sql, [response.store_id, response.events_recaps_id, JSON.stringify(response)])
      } catch (error) {
        logInfo('error', 'writeStoreRecapResponses', 'Error writing store recap response', {
          error,
          response,
        })
        failedResponses.push(response)
      }
    }
    return !(failedResponses && failedResponses.length)
  }

  // Remove any images over 14 days old
  async function removeStaleStoreRecapResponses() {
    try {
      const sql = `
        DELETE FROM store_recap_responses WHERE date_uploaded <= date('now','-14 day')
      `
      await run(sql, [])
    } catch (error) {
      logInfo(
        'error',
        'removeStaleStoreRecapResponses',
        'Error removing stale store recap responses',
        { error }
      )
    }
  }

  async function removeStoreResponsesForSyncedResponseWithStoreId(
    store_id: string,
    recap_id: string
  ) {
    try {
      const sql = `
        DELETE FROM store_recap_responses WHERE store_id = ? AND recap_id = ?
      `
      await run(sql, [store_id, recap_id])
      const indexes = recapStore.recap_responses.value
        .map((response, index) => {
          return response.store_id === store_id && response.events_recaps_id === recap_id
            ? index
            : null
        })
        .filter((index) => index !== null)
        .reverse() // We need to reverse this so we can remove each response by index
      for (const idx of indexes) {
        if (idx !== null) {
          recapStore.recap_responses.value.splice(+idx, 1)
        }
      }
    } catch (error) {
      logInfo(
        'error',
        'removeStoreResponsesForSyncedResponseWithStoreId',
        'Error removing store responses for synced response',
        { error, store_id, recap_id }
      )
    }
  }

  async function clearStoreRecapResponses() {
    try {
      const sql = `
        DELETE FROM store_recap_responses
      `
      await run(sql, [])
      recapStore.recap_responses.value.splice(0, recapStore.recap_responses.value.length)
    } catch (error) {
      logInfo('error', 'clearStoreRecapResponses', 'Error clearing store recap responses', {
        error,
      })
    }
  }

  return {
    getAllStoreRecapResponses,
    getStoreRecapResponseForStoreAndRecap,
    writeStoreRecapResponses,
    removeStaleStoreRecapResponses,
    removeStoreResponsesForSyncedResponseWithStoreId,
    clearStoreRecapResponses,
  }
}
